import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { adminNav } from '@/components/px/nav'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  credentialErrorDismissed,
  credentialsRetryClicked,
  fetchCredentials,
  fetchProjectConnectorHealth,
  projectHealthRetryClicked,
  revokeCredential,
  runHealthCheck,
  saveCredential,
  selectCredentialMutationError,
  selectCredentials,
  selectCredentialsCache,
  selectCredentialsError,
  selectInFlightKey,
  selectProjectConnectorHealth,
  selectProjectConnectorHealthCache,
  selectProjectConnectorHealthError,
  startReconnect
} from '@/features/credentials/credentialsSlice'
import type {
  ConnectorHealthEntry,
  OutboundCredentialHealth,
  OutboundCredentialInput,
  OutboundCredentialMetadata
} from '@/features/credentials/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { useAppDispatch, useAppSelector } from '@/store'

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

/** The SPA route the provider redirects back to after consent. */
const OAUTH_CALLBACK_ROUTE = '/admin/connectors/callback'

/** Health status → canonical pill tone (words always render; never colour alone). */
const HEALTH_TONE: Record<OutboundCredentialHealth['status'], StatusTone> = {
  ok: 'done',
  degraded: 'review',
  down: 'blocked'
}

const PROJECT_HEALTH_TONE: Record<ConnectorHealthEntry['status'], StatusTone> = {
  ok: 'done',
  degraded: 'review',
  down: 'blocked',
  unknown: 'neutral'
}

/** The health cell: the three tones, or the explicit "never checked" state. */
function HealthPill({ lastHealth }: { lastHealth: OutboundCredentialHealth | null }) {
  const { t } = useTranslation('connectors')
  if (lastHealth === null) {
    return (
      <StatusPill tone="neutral">
        <span aria-hidden>—</span>
        {t('health.neverChecked')}
      </StatusPill>
    )
  }
  const tone = HEALTH_TONE[lastHealth.status]
  return (
    <span className="flex flex-col items-start gap-0.5">
      <StatusPill tone={tone}>
        {t(`health.status.${lastHealth.status}`)}
      </StatusPill>
      {lastHealth.detail !== null && lastHealth.detail !== '' && (
        <span className="max-w-[14rem] text-xs text-muted-foreground" title={lastHealth.detail}>
          {lastHealth.detail}
        </span>
      )}
    </span>
  )
}

export interface CredentialFormValues {
  provider: string
  subject: string
  secret: string
  tenantExternalId: string
}

const EMPTY_FORM: CredentialFormValues = {
  provider: '',
  subject: '',
  secret: '',
  tenantExternalId: ''
}

/**
 * The create/rotate form. `secret` is a WRITE-ONLY field (INV-1): it exists
 * only in this component's local state, is cleared on every successful save,
 * and never reaches Redux, storage or a log line.
 */
function CredentialForm({
  initial,
  submitting,
  onSubmitClicked,
  onCancelClicked
}: {
  /** Rotation pre-fills provider+subject only — never any secret material. */
  initial: CredentialFormValues
  submitting: boolean
  onSubmitClicked: (values: CredentialFormValues) => void
  onCancelClicked: () => void
}) {
  const { t } = useTranslation(['connectors', 'common'])
  const [values, setValues] = useState<CredentialFormValues>(initial)

  const onFieldChanged = (field: keyof CredentialFormValues, value: string): void => {
    setValues((current) => ({ ...current, [field]: value }))
  }

  const valid = values.provider.trim() !== '' && values.secret !== ''

  return (
    <form
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (valid) onSubmitClicked(values)
      }}
    >
      <label className="flex flex-col gap-1 text-sm text-ink-soft">
        {t('form.provider')}
        <input
          type="text"
          value={values.provider}
          onChange={(event) => onFieldChanged('provider', event.target.value)}
          className={`${inputClass} w-52 font-mono text-xs`}
          required
        />
      </label>
      <label className="flex flex-col gap-1 text-sm text-ink-soft">
        {t('form.subject')}
        <input
          type="text"
          value={values.subject}
          onChange={(event) => onFieldChanged('subject', event.target.value)}
          className={`${inputClass} w-52 font-mono text-xs`}
        />
        <span className="text-xs text-muted-foreground">{t('form.subjectHint')}</span>
      </label>
      <label className="flex flex-col gap-1 text-sm text-ink-soft">
        {t('form.secret')}
        <input
          type="password"
          autoComplete="new-password"
          value={values.secret}
          onChange={(event) => onFieldChanged('secret', event.target.value)}
          className={`${inputClass} w-52`}
          required
        />
        <span className="text-xs text-muted-foreground">{t('form.secretHint')}</span>
      </label>
      <label className="flex flex-col gap-1 text-sm text-ink-soft">
        {t('form.tenantExternalId')}
        <input
          type="text"
          value={values.tenantExternalId}
          onChange={(event) => onFieldChanged('tenantExternalId', event.target.value)}
          className={`${inputClass} w-52 font-mono text-xs`}
        />
        <span className="text-xs text-muted-foreground">{t('form.tenantHint')}</span>
      </label>
      <div className="flex items-end gap-2">
        <button
          type="submit"
          disabled={!valid || submitting}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {submitting ? t('common:states.loading') : t('form.submit')}
        </button>
        <button
          type="button"
          onClick={onCancelClicked}
          className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
        >
          {t('common:actionsMore.cancel')}
        </button>
      </div>
    </form>
  )
}

/** The delete confirm step — a revoke is an explicit act (AC-3). */
function ConfirmRevokeDialog({
  provider,
  subject,
  onConfirmClicked,
  onCancelClicked
}: {
  provider: string
  subject: string | null
  onConfirmClicked: () => void
  onCancelClicked: () => void
}) {
  const { t } = useTranslation(['connectors', 'common'])
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="revoke-confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-card px-4 py-4 shadow-lg">
        <h2 id="revoke-confirm-title" className="font-serif text-lg font-semibold">
          {t('revoke.title')}
        </h2>
        <p className="mt-1.5 text-sm text-ink-soft">
          {t('revoke.body', {
            provider,
            subject: subject ?? t('revoke.appOnly')
          })}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancelClicked}
            className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
          >
            {t('common:actionsMore.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirmClicked}
            className="rounded-md bg-blocked px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            {t('common:actionsMore.delete')}
          </button>
        </div>
      </div>
    </div>
  )
}

/** The narrow project-member projection — exactly what the server sends. */
function ProjectHealthRow({ row }: { row: ConnectorHealthEntry }) {
  const { t } = useTranslation(['connectors', 'common'])
  return (
    <TableRow>
      <TableHead scope="row" className="h-auto py-2.5 pl-3 align-top font-normal">
        <span className="font-mono text-xs text-foreground">{row.provider}</span>
      </TableHead>
      <TableCell className="py-2.5 align-top">
        <StatusPill tone={PROJECT_HEALTH_TONE[row.status]}>
          {t(`health.status.${row.status}`)}
        </StatusPill>
      </TableCell>
      <TableCell className="py-2.5 pr-3 align-top text-sm text-ink-soft">
        {row.checkedAt === null || row.checkedAt === undefined
          ? t('health.neverChecked')
          : new Date(row.checkedAt).toLocaleString()}
      </TableCell>
    </TableRow>
  )
}

/**
 * `/admin/connectors` (PX-W6.5, AC-3 + AC-4) — the org-admin credential
 * table (create/rotate/delete/health-check + the org-wide OAuth reconnect)
 * and the project-scoped connector-health panel any project member can read.
 */
export default function ConnectorsPage() {
  const { t } = useTranslation(['connectors', 'common'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const credentials = useAppSelector(selectCredentials)
  const cache = useAppSelector(selectCredentialsCache)
  const error = useAppSelector(selectCredentialsError)
  const mutationError = useAppSelector(selectCredentialMutationError)
  const inFlightKey = useAppSelector(selectInFlightKey)
  const projectHealth = useAppSelector(selectProjectConnectorHealth)
  const projectHealthCache = useAppSelector(selectProjectConnectorHealthCache)
  const projectHealthError = useAppSelector(selectProjectConnectorHealthError)

  const [formOpen, setFormOpen] = useState(false)
  const [formInitial, setFormInitial] = useState<CredentialFormValues>(EMPTY_FORM)
  const [revokeTarget, setRevokeTarget] = useState<OutboundCredentialMetadata | null>(null)
  const [healthProjectId, setHealthProjectId] = useState('')

  useEffect(() => {
    void dispatch(fetchCredentials())
  }, [dispatch])

  useEffect(() => {
    if (healthProjectId !== '') {
      void dispatch(fetchProjectConnectorHealth(healthProjectId))
    }
  }, [dispatch, healthProjectId])

  /** The input built at submit time; the secret lives only in the closure. */
  const onFormSubmitted = (values: CredentialFormValues): void => {
    const input: OutboundCredentialInput = {
      secret: values.secret,
      ...(values.tenantExternalId.trim() !== '' ? { tenantExternalId: values.tenantExternalId } : {})
    }
    void dispatch(
      saveCredential({
        provider: values.provider.trim(),
        subject: values.subject.trim() === '' ? null : values.subject.trim(),
        input
      })
    )
      .unwrap()
      .then(() => {
        // INV-1: the write-only field clears on success and never re-renders.
        setFormOpen(false)
        setFormInitial(EMPTY_FORM)
      })
      .catch(() => {
        // The mapped mutationError renders below; the form keeps its values
        // EXCEPT nothing re-populates the secret from anywhere.
      })
  }

  const onRotateClicked = (row: OutboundCredentialMetadata): void => {
    setFormInitial({
      provider: row.provider,
      subject: row.subject ?? '',
      secret: '',
      tenantExternalId: ''
    })
    setFormOpen(true)
  }

  return (
    <AppShell
      moduleLabel={t('chrome.moduleLabel')}
      breadcrumb={<span>{t('chrome.breadcrumb')}</span>}
      contextPill={t('chrome.contextPill')}
      nav={adminNav('admin')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">{t('lede')}</p>
      </header>

      <section aria-label={t('form.heading')} className="mt-4">
        {formOpen ? (
          <div className="rounded-lg border border-border bg-card px-4 py-4">
            <SectionLabel>{t('form.heading')}</SectionLabel>
            <p className="mt-1 text-xs text-muted-foreground">{t('form.helper')}</p>
            <div className="mt-3">
              <CredentialForm
                key={`${formInitial.provider}:${formInitial.subject}`}
                initial={formInitial}
                submitting={inFlightKey !== null}
                onSubmitClicked={onFormSubmitted}
                onCancelClicked={() => {
                  setFormOpen(false)
                  setFormInitial(EMPTY_FORM)
                  dispatch(credentialErrorDismissed())
                }}
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setFormInitial(EMPTY_FORM)
              setFormOpen(true)
            }}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {t('form.newCredential')}
          </button>
        )}
      </section>

      {mutationError !== null && (
        <p role="alert" className="mt-3 text-sm text-blocked">
          {toMessage(mutationError)}
        </p>
      )}

      <section className="mt-4 rounded-lg border border-border bg-card" aria-label={t('table.caption')}>
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <div className="p-4">
            <ForbiddenPanel requested="/{orgId}/outbound-credentials" />
          </div>
        ) : error !== null ? (
          <div className="p-4">
            <ErrorPanel
              error={error}
              onRetryClicked={() => {
                dispatch(credentialsRetryClicked())
                void dispatch(fetchCredentials())
              }}
            />
          </div>
        ) : credentials.length === 0 ? (
          <EmptyPanel body={t('empty.credentials')} />
        ) : (
          <Table>
            <TableCaption className="sr-only">{t('table.caption')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={HEAD_CLASS + ' pl-3'}>
                  {t('table.columns.provider')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.subject')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.grant')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.scopes')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.expires')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.state')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.health')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS + ' pr-3 text-right'}>
                  {t('table.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {credentials.map((row) => {
                const key = `${row.provider}::${row.subject ?? ''}`
                const busy = inFlightKey === key
                const dead = !row.active || row.lastHealth?.status === 'down'
                const { scopes = [] } = row
                return (
                  <TableRow key={key}>
                    <TableHead scope="row" className="h-auto py-2.5 pl-3 align-top font-normal">
                      <span className="font-mono text-xs text-foreground">{row.provider}</span>
                      {row.tenantExternalId !== null && row.tenantExternalId !== '' && (
                        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                          {row.tenantExternalId}
                        </span>
                      )}
                    </TableHead>
                    <TableCell className="py-2.5 align-top text-sm text-ink-soft">
                      {row.subject ?? t('table.appOnly')}
                    </TableCell>
                    <TableCell className="py-2.5 align-top font-mono text-xs text-ink-soft">
                      {row.grantType}
                    </TableCell>
                    <TableCell className="max-w-[12rem] py-2.5 align-top text-xs text-muted-foreground">
                      {scopes.join(', ')}
                    </TableCell>
                    <TableCell className="py-2.5 align-top text-sm text-ink-soft">
                      {row.expiresAt === null || row.expiresAt === undefined
                        ? t('common:values.never')
                        : new Date(row.expiresAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="py-2.5 align-top">
                      <StatusPill tone={row.active ? 'done' : 'blocked'}>
                        {row.active
                          ? t('table.active')
                          : t('table.revoked', {
                              at:
                                row.revokedAt === null || row.revokedAt === undefined
                                  ? ''
                                  : new Date(row.revokedAt).toLocaleDateString()
                            })}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="py-2.5 align-top">
                      <HealthPill lastHealth={row.lastHealth ?? null} />
                    </TableCell>
                    <TableCell className="py-2.5 pr-3 align-top">
                      <span className="flex flex-wrap justify-end gap-1.5">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void dispatch(runHealthCheck({ provider: row.provider, subject: row.subject }))}
                          className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted disabled:opacity-60"
                        >
                          {t('actions.check')}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onRotateClicked(row)}
                          className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted disabled:opacity-60"
                        >
                          {t('actions.rotate')}
                        </button>
                        {dead && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              void dispatch(
                                startReconnect({
                                  provider: row.provider,
                                  body: {
                                    subject: row.subject ?? undefined,
                                    redirectAfter: OAUTH_CALLBACK_ROUTE
                                  }
                                })
                              )
                            }
                            className="rounded-md border border-input px-2 py-1 text-xs text-primary hover:bg-muted disabled:opacity-60"
                          >
                            {t('actions.reconnect')}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setRevokeTarget(row)}
                          className="rounded-md border border-input px-2 py-1 text-xs text-blocked hover:bg-muted disabled:opacity-60"
                        >
                          {t('actions.delete')}
                        </button>
                      </span>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </section>

      <section aria-label={t('projectHealth.heading')} className="mt-6">
        <SectionLabel>{t('projectHealth.heading')}</SectionLabel>
        <p className="mt-1 max-w-3xl text-xs text-muted-foreground">{t('projectHealth.lede')}</p>
        <label className="mt-2 flex w-64 flex-col gap-1 text-sm text-ink-soft">
          {t('projectHealth.projectId')}
          <input
            type="text"
            value={healthProjectId}
            onChange={(event) => setHealthProjectId(event.target.value)}
            placeholder={t('projectHealth.placeholder')}
            className={inputClass}
          />
        </label>

        <div className="mt-3 rounded-lg border border-border bg-card">
          {healthProjectId === '' ? (
            <EmptyPanel body={t('projectHealth.pickFirst')} />
          ) : projectHealthCache.loading ? (
            <LoadingPanel />
          ) : projectHealthError !== null && projectHealthError.status === 403 ? (
            <div className="p-4">
              <ForbiddenPanel requested="/{orgId}/projects/:projectId/connectors/health" />
            </div>
          ) : projectHealthError !== null ? (
            <div className="p-4">
              <ErrorPanel
                error={projectHealthError}
                onRetryClicked={() => {
                  dispatch(projectHealthRetryClicked())
                  void dispatch(fetchProjectConnectorHealth(healthProjectId))
                }}
              />
            </div>
          ) : projectHealth.length === 0 ? (
            <EmptyPanel body={t('projectHealth.empty')} />
          ) : (
            <Table>
              <TableCaption className="sr-only">{t('projectHealth.caption')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col" className={HEAD_CLASS + ' pl-3'}>
                    {t('table.columns.provider')}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS}>
                    {t('table.columns.health')}
                  </TableHead>
                  <TableHead scope="col" className={HEAD_CLASS + ' pr-3'}>
                    {t('projectHealth.checkedAt')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projectHealth.map((row) => (
                  <ProjectHealthRow key={row.provider} row={row} />
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>

      {revokeTarget !== null && (
        <ConfirmRevokeDialog
          provider={revokeTarget.provider}
          subject={revokeTarget.subject}
          onConfirmClicked={() => {
            const target = revokeTarget
            setRevokeTarget(null)
            void dispatch(
              revokeCredential({ provider: target.provider, subject: target.subject })
            )
          }}
          onCancelClicked={() => setRevokeTarget(null)}
        />
      )}

      <p className="mt-6 border-t border-border pt-3 text-xs italic text-muted-foreground">
        {t('densityNote')}
      </p>
    </AppShell>
  )
}
